// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import validatePassword from "@insite/client-framework/Store/CommonHandlers/ValidatePassword";
import translate from "@insite/client-framework/Translate";
import Button from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import PasswordRequirementsValidator from "@steiner/App/Components/Common/PasswordRequirements";
import FloatingTooltip, { FloatingTooltipStyles } from "@steiner/App/Components/FloatingTooltip/FloatingTooltip";
import { getSettingsCollection } from "@steiner/App/Store/Selectors/SettingsSelectors";
import TextField from "@steiner/MobiusOverrides/TextField/TextField";
import { placeHolders } from "@steiner/XDL/constants";
import { LoginInformationFields } from "@steiner/XDL/types";
import { useValidate, xDefaultRules } from "@steiner/XDL/Utilities";
import { FormikProps } from "formik";
import React, { forwardRef, ReactNode, Ref, useImperativeHandle, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

type Props = {
    onSubmit: (values: LoginInformationFields) => void;
} & ResolveThunks<typeof mapDispatchToProps>;

const mapDispatchToProps = {
    validatePassword,
};

export interface LoginInfoFormStyles {
    container?: GridContainerProps;
    fullWidthGridItem?: GridItemProps;
    actionsGridItem?: GridItemProps;
    floatingTooltipGrid?: GridItemProps;
    xngTooltip?: FloatingTooltipStyles;
}

export const loginInformationStyles: LoginInfoFormStyles = {};

const styles = loginInformationStyles;

const LoginInformationForm = ({ onSubmit, validatePassword }: Props, ref: Ref<FormikProps<LoginInformationFields>>) => {
    const [isMenuOpen, setMenuOpen] = useState(false);
    const [passwordErrorMessage, setPasswordErrorMessage] = useState<ReactNode>("");
    const { accountSettings } = useAppSelector(state => ({
        accountSettings: getSettingsCollection(state).accountSettings,
    }));
    const {
        passwordMinimumLength,
        passwordRequiresUppercase,
        passwordRequiresSpecialCharacter,
        passwordRequiresLowercase,
        passwordRequiresDigit,
    } = accountSettings;

    const LoginInfoValidators = {
        emailAddress: `required|${xDefaultRules.email}`,
        password: `required|password`,
        confirmPassword: `required|${xDefaultRules.password}|confirm:password+${translate("Password")}`,
    };

    const { field, handleSubmit, formik, isSubmitting } = useValidate({
        initialValues: {
            emailAddress: "",
            password: "",
            confirmPassword: "",
        },
        validators: LoginInfoValidators,
        onSubmit: (fieldValues: LoginInformationFields, { setSubmitting }: any) => {
            if (passwordErrorMessage) {
                setSubmitting(false);
                return;
            }
            setSubmitting(false);
            submitHandler(fieldValues);
        },
    });

    const submitHandler = (fieldValues: LoginInformationFields) => {
        onSubmit(fieldValues);
    };

    useImperativeHandle(ref, () => ({
        ...formik,
    }));

    const handleInputFocus = () => {
        setMenuOpen(true);
    };

    const handleInputBlur = () => {
        setMenuOpen(false);
    };

    const passwordChangeHandler = (newPassword: string) => {
        formik.setFieldValue("password", newPassword); // Update password field value
        validatePassword({
            password: newPassword,
            onComplete: errorMessage => {
                setPasswordErrorMessage(errorMessage);
            },
        });
    };

    return (
        <LoadingOverlay loading={isSubmitting}>
            <GridContainer {...styles.container}>
                <GridItem {...styles.fullWidthGridItem}>
                    <TextField
                        {...field("emailAddress")}
                        label={translate("Email Address (Username)")}
                        placeholder={placeHolders.email}
                        error={field("emailAddress").error && "A valid email address is required"}
                    />
                </GridItem>
                <GridItem {...styles.floatingTooltipGrid}>
                    <FloatingTooltip
                        offset={16}
                        onOpenChange={open => setMenuOpen(open)}
                        trigger="manual"
                        placement="bottom-start"
                        open={isMenuOpen}
                        showArrow={false}
                        overlay={false}
                        triggerComponent={
                            <TextField
                                {...field("password")}
                                label={translate("Password")}
                                placeholder={translate("Password")}
                                onChange={event => passwordChangeHandler(event.target.value)}
                                type="password"
                                error={passwordErrorMessage}
                                onFocus={handleInputFocus}
                                onBlur={handleInputBlur}
                            />
                        }
                        extendedStyles={styles.xngTooltip}
                    >
                        <PasswordRequirementsValidator
                            password={field("password").value}
                            passwordMinimumLength={passwordMinimumLength}
                            passwordRequiresDigit={passwordRequiresDigit}
                            passwordRequiresLowercase={passwordRequiresLowercase}
                            passwordRequiresUppercase={passwordRequiresUppercase}
                            passwordRequiresSpecialCharacter={passwordRequiresSpecialCharacter}
                            passwordNotAllowedSpecialCharacters={true}
                        />
                    </FloatingTooltip>
                </GridItem>
                <GridItem {...styles.fullWidthGridItem}>
                    <TextField
                        {...field("confirmPassword")}
                        label={translate("Confirm Password")}
                        type="password"
                        placeholder={translate("Confirm Password")}
                        error={field("confirmPassword").error && "Confirm password must match the password"}
                    />
                </GridItem>
            </GridContainer>
            <StyledWrapper {...styles.actionsGridItem} className="form-buttons">
                <Button type="button" onClick={handleSubmit as any} className="stepperNextButton-1">
                    {translate("Next")}
                </Button>
            </StyledWrapper>
        </LoadingOverlay>
    );
};

export default connect(null, mapDispatchToProps)(forwardRef(LoginInformationForm));
